// import { useDispatch } from 'react-redux';
import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import _ from 'lodash'
import { allRoutes } from '../constants/config'

import axios from 'axios';
import { COUNT_FLAGS } from '../constants/countFlags';

const {
  GET_API_USER_ASSET_TREE, GET_API_USER_COUNTS, GET_API_USER_LIT_GRP_TYPES, GET_API_LIT_GROUP_TREE,
  ADD_API_LIT_GROUP_AND_BOOKS, ADD_API_LIT_GROUP_BOOK, DELETE_API_LIT_GROUP,
  ADD_API_UPDATE_LIT_GROUP, GET_API_ADD_CHAR_TO_BOOK,
  ADD_VARIATION_TO_COMMON_LIST, ADD_VARIATION_TO_BOOK_CHARACTER, GET_BOOK_DATA,
  GET_SPEECH_SAMPLE_DATA, GET_VARIATION_LIST,
  GET_BOOK_CHAR_VAR_SAMPLES, GET_CHAR_VAR_SAMPLES,
  // Direct fetch endpoints
  GET_API_DIRECT_LIT_GROUPS, GET_API_DIRECT_LIT_GROUP_PROFILE, GET_API_DIRECT_BOOKS_BY_LIT_GROUP,
  GET_API_DIRECT_CHARACTERS_BY_BOOK, GET_API_DIRECT_VARIATIONS_BY_CHARACTER
} = { ...allRoutes }

export const FIELD_BITS = {
  INITIALIZE: 0x3F,
  AUTHOR_BIT: 0x01,
  BOOK_SERIES: 0x02,
  BOOK_TITLE: 0x04,
  CHARACTER_NAME: 0x08,
  EMOTION_VARAIATION: 0x10,
  LITERARY_GROUP_TYPE: 0x20,
  SUBMIT_ENABLE: 0x40,
}

export const defaultCharSpeechData = {
  uuid: '',
  speechSample: {
    identity: '', labels: [], elementId: '', uuid: '',
    title: '',
    revision: '',
    textSnippetUuid: '',
    voiceSnippetUuid: '',
    isSelected: false,
    enabled: false,
  },
  character: {
    identity: '', labels: [], elementId: '', uuid: '',
    name: '',
    description: '',
    aaiType: '',
    createdAt: '',
    updatedAt: '',
    isActive: false,
    status: '',
  },
  textSnippet: {
    identity: '', labels: [], elementId: '', uuid: '',
    fileName: '',
    variation: '',
    version: 0,
    s3TextUrl: '',
    createdAt: '',
  },
  voiceSnippet: {
    identity: '', labels: [], elementId: '', uuid: '',
    fileName: '',
    variation: '',
    version: 0,
    s3Url: '',
    createdAt: '',
  },
  variation: {
    identity: '', labels: [], elementId: '', uuid: '',
    name: '',
    value: '',
  },
};


export const fetchRelatedData = createAsyncThunk(
  'userLitGroupAssets/fetchRelatedData',
  async (litGroupUuid, { dispatch, getState }) => {
    // Fetch related data based on litGroupUuid
    // For example, you might call an API or perform some other asynchronous operation

    // Assuming deriveLitGroupElements is a function that returns structured dropdown options
    const litGroup = getState().user.userLitGroupAssets.userLitGroupData.has_lit_groups[0].has_lit_group.find(group => group.uuid === litGroupUuid);
    const dropdownOptions = deriveLitGroupElements(litGroup);
    // console.log('dropdownOptions', dropdownOptions)
    return dropdownOptions; // Return the structured dropdown options
  }
);

export const ensureStdLitGrpTypes = createAsyncThunk(
  'userStdLitGrpTypes/ensureFetch',
  async (token, { getState, dispatch }) => {
    const { litGrpTypes, stdLitGrpTypesStatus } = selectStdLitGroupTypesState(getState());
    if (stdLitGrpTypesStatus !== 'succeeded') {
      return await dispatch(fetchUserStdLitGrpTypes(token));
    }
    return litGrpTypes;
  }
);

export const fetchUserAssetTree = createAsyncThunk(
  'userAssets/fetch',
  async (token, { rejectWithValue }) => {
    // console.log('fetchUserAssetTree GET_API_USER_ASSET_TREE', GET_API_USER_ASSET_TREE)
    try {
      const response = await axios.get(GET_API_USER_ASSET_TREE, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (err) {

      return rejectWithValue(err.response.data);
    }
  }
);

// New thunk to fetch user counts with dynamic flags
export const fetchUserCounts = createAsyncThunk(
  'userAssets/fetchCounts',
  async ({ flags = COUNT_FLAGS.DASHBOARD_VIEW, token }, { rejectWithValue }) => {
    try {
      const response = await axios.get(GET_API_USER_COUNTS, {
        params: { flags },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Error fetching user counts');
    }
  }
);

// New thunk to fetch user assets while preserving selections
export const fetchUserAssetsPreserveSelections = createAsyncThunk(
  'userLitGroupAssets/fetchUserAssetsPreserveSelections',
  async (_, { dispatch, getState }) => {
    // Get current selections before fetching
    const state = getState();
    const currentLitGroup = selectSelectedLitGroupProfile(state);
    const currentLitGroupUuid = selectSelectedLitGroupUuid(state);
    const currentBook = selectSelectedBook(state);
    const currentCharacter = selectSelectedCharacter(state);

    // Store these for later use
    const selectionsToPreserve = {
      litGroup: currentLitGroup ? { uuid: currentLitGroupUuid } : null,
      book: currentBook,
      character: currentCharacter
    };

    console.log('Preserving selections before fetch:', selectionsToPreserve);

    try {
      // Get the auth token from localStorage
      const token = localStorage.getItem('authToken');

      // Fetch user assets
      const result = await dispatch(fetchUserAssetTree(token)).unwrap();

      // Re-apply selections after fetch completes
      if (selectionsToPreserve.litGroup) {
        console.log('Re-applying litGroup selection:', selectionsToPreserve.litGroup);
        dispatch(setSelection({ level: 'litGroup', id: selectionsToPreserve.litGroup.uuid }));
      }

      if (selectionsToPreserve.book) {
        console.log('Re-applying book selection:', selectionsToPreserve.book);
        dispatch(setSelection({
          level: 'book',
          id: selectionsToPreserve.book.uuid,
          data: { name: selectionsToPreserve.book.name }
        }));
      }

      if (selectionsToPreserve.character) {
        console.log('Re-applying character selection:', selectionsToPreserve.character);
        dispatch(setSelection({ level: 'character', id: selectionsToPreserve.character.uuid }));
      }

      return result;
    } catch (error) {
      console.error('Error in fetchUserAssetsPreserveSelections:', error);
      throw error;
    }
  }
);

export const fetchUserStdLitGrpTypes = createAsyncThunk(
  'userStdLitGrpTypes/fetch',
  async (token, { rejectWithValue }) => {
    try {
      const response = await axios.get(GET_API_USER_LIT_GRP_TYPES, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

// GET_API_LIT_GROUP_TREE
export const fetchUserLitGrpTree = createAsyncThunk(
  'userLitGrpTree/fetch',
  async ({ litGroupUuid }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(GET_API_LIT_GROUP_TREE, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          litGroupUuid: litGroupUuid,
        }
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);


// Add a new LitGroups Root (if not found), LitGroup (defines new group), and list of BookDetails
export const addLitGroupAndBooks = createAsyncThunk(
  'litGroup/addLitGroupAndBooks',
  async ({ collectionsUuid, collectionDetails, bookDetails }) => {
    const response = await axios.post(ADD_API_LIT_GROUP_AND_BOOKS, {
      collectionsUuid,
      collectionDetails,
      bookDetails
    });
    return response.data;
  }
);

// Add individual book to existing LitGroup
export const addLitGroupBook = createAsyncThunk(
  'litGroup/addLitGroupBook',
  async ({ collectionsUuid, bookDetail }) => {
    const response = await axios.post(ADD_API_LIT_GROUP_BOOK, {
      collectionsUuid,
      bookDetail
    });
    return response.data;
  }
);

// Delete LitGroup (remove the whole group and its books)
export const deleteLitGroup = createAsyncThunk(
  'litGroup/deleteitGroup',
  async ({ collectionsUuid }) => {
    const response = await axios.post(DELETE_API_LIT_GROUP, {
      collectionsUuid
    });
    return response.data;
  }
);



// try {
//   const response = await Axios.post('http://localhost:3000/api/book', {
//     bookTitle,
//     bookData,
//   });
//   console.log(response.data); // Optional: Handle the server's response
// } catch (error) {
//   console.error(error);ADD_API_UPDATE_LIT_GROUP
// }
export const addUpdateLitGroup = createAsyncThunk(
  'litGroup/addUpdateLitGroup',
  async ({ collectionsUuid }) => {
    const response = await axios.post(ADD_API_UPDATE_LIT_GROUP, {
      collectionsUuid
    });
    return response.data;
  }
)

// Add individual book to existing LitGroup
// export const addCharacterToBook = createAsyncThunk(
//   'litGroup/addCharacterToBook',
//   async ({ userId, book_uuid, char_uuid }) => {
//     const response = await axios.get(config.GET_API_ADD_CHAR_TO_BOOK, {
//       userId,
//       book_uuid,
//       char_uuid
//     });
//     return response.data;
//   }
// );
export const addCharacterToBook = createAsyncThunk(
  'userLitGrpTree/addCharacterToBook',
  async ({ userId, book_uuid, char_uuid }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(GET_API_ADD_CHAR_TO_BOOK, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          userId: userId,
          book_uuid: book_uuid,
          char_uuid: char_uuid
        }
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);


//ADD_VARIATION_TO_COMMON_LIST
// Add character voice variation (like happy, sad, geeky) to the list of commmon variations of a litGroup
export const addVariationToCommonList = createAsyncThunk(
  'userLitGrpTree/addVariationToCommonList', // Corrected action name to match function's purpose
  async ({ userId, lit_group_uuid, variation_uuid, variation_name }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      // In axios.post, the URL is the first parameter, the data (body) is the second parameter, and the config is the third parameter.
      const response = await axios.post(ADD_VARIATION_TO_COMMON_LIST, {
        userId,
        lit_group_uuid,
        variation_uuid,
        variation_name
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

export const addVariationToBookCharacter = createAsyncThunk(
  'userLitGrpTree/addVariationToBookCharacter', // Corrected action name to match function's purpose
  async ({ userId, book_uuid, character_uuid, lit_group_uuid, variation_uuid, variation_name }, { dispatch, rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      // In axios.post, the URL is the first parameter, the data (body) is the second parameter, and the config is the third parameter.
      const response = await axios.post(ADD_VARIATION_TO_BOOK_CHARACTER, {
        userId,
        lit_group_uuid,
        book_uuid,
        character_uuid,
        variation_uuid,
        variation_name
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      await dispatch(fetchBookData({ bookuuid: book_uuid }));

      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);


export const fetchBookData = createAsyncThunk(
  'userLitGrpTree/getBookData',
  async ({ bookuuid }, { rejectWithValue }) => {
    try {
      const response = await axios.get(GET_BOOK_DATA, {
        params: {
          bookuuid: bookuuid,
        },
      });

      // Check if HAS_CHARACTER exists and sort it by name
      if (
        response.data.bookTree?.HAS_BOOK_CHARACTERS?.[0]?.HAS_CHARACTER
      ) {
        response.data.bookTree.HAS_BOOK_CHARACTERS[0].HAS_CHARACTER =
          response.data.bookTree.HAS_BOOK_CHARACTERS[0].HAS_CHARACTER
            .slice() // Create a shallow copy
            .sort((a, b) => a.name.localeCompare(b.name)); // Sort by name
      }

      // console.log('Sorted Character List:', response.data.bookTree.HAS_BOOK_CHARACTERS[0]?.HAS_CHARACTER);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Error fetching book data');
    }
  }
);


export const fetchSpeechSampleData = createAsyncThunk(
  'userLitGrpTree/getSpeechSampleData',
  async ({ userId, sampleUuid }, { rejectWithValue }) => {
    try {
      // const token = localStorage.getItem('authToken');
      const response = await axios.get(GET_SPEECH_SAMPLE_DATA, {
        // headers: {
        //   Authorization: `Bearer ${token}`,
        // },
        params: {
          userId: userId,
          sampleUuid: sampleUuid,
        }
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

export const updateS3UrlData = createAsyncThunk(
  'userLitGrpTree/updateS3UrlData',
  async (s3Url, { rejectWithValue }) => {
    try {
      const response = await axios.get(s3Url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
  }
);
// GET_API_LIT_GROUP_TREE
export const fetcLitGrpVariationList = createAsyncThunk(
  'userLitGrpTree/getVariationList',
  async ({ litGroupUuid }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(GET_VARIATION_LIST, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          litGroupUuid: litGroupUuid,
        }
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

export const fetchBookCharsVariationSamples = createAsyncThunk(
  'userLitGrpTree/getBookCharsVariationSamples',
  async ({ litGroupUuid, bookUuid }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(GET_BOOK_CHAR_VAR_SAMPLES, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          litGroupUuid: litGroupUuid,
          bookUuid: bookUuid,
        }
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

export const fetchCharVariationSamples = createAsyncThunk(
  'userLitGrpTree/getCharVariationSamples',
  async ({ litGroupUuid, bookUuid, charUuid }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(GET_CHAR_VAR_SAMPLES, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          litGroupUuid: litGroupUuid,
          bookUuid: bookUuid,
          charUuid: charUuid,
        }
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

// New thunks for direct fetching
export const fetchLitGroupsDirect = createAsyncThunk(
  'userLitGroupAssets/fetchLitGroupsDirect',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(GET_API_DIRECT_LIT_GROUPS, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Error fetching literary groups');
    }
  }
);

export const fetchBooksByLitGroup = createAsyncThunk(
  'userLitGroupAssets/fetchBooksByLitGroup',
  async (litGroupUuid, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      // Replace the parameter in the URL
      const url = GET_API_DIRECT_BOOKS_BY_LIT_GROUP.replace(':litGroupUuid', litGroupUuid);
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Error fetching books');
    }
  }
);

export const fetchCharactersByBook = createAsyncThunk(
  'userLitGroupAssets/fetchCharactersByBook',
  async (bookUuid, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      // Replace the parameter in the URL
      const url = GET_API_DIRECT_CHARACTERS_BY_BOOK.replace(':bookUuid', bookUuid);
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Error fetching characters');
    }
  }
);

export const fetchVariationsByCharacter = createAsyncThunk(
  'userLitGroupAssets/fetchVariationsByCharacter',
  async (characterUuid, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      // Replace the parameter in the URL
      const url = GET_API_DIRECT_VARIATIONS_BY_CHARACTER.replace(':characterUuid', characterUuid);
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Error fetching variations');
    }
  }
);

export const fetchLitGroupProfile = createAsyncThunk(
  'userLitGroupAssets/fetchLitGroupProfile',
  async (litGroupUuid, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      // Replace the parameter in the URL
      const url = GET_API_DIRECT_LIT_GROUP_PROFILE.replace(':litGroupUuid', litGroupUuid);
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Error fetching literary group profile');
    }
  }
);

// New thunks for BookProfile
export const fetchBookProfile = createAsyncThunk(
  'userLitGroupAssets/fetchBookProfile',
  async (bookUuid, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      // Replace the parameter in the URL
      const url = allRoutes.GET_API_BOOK_PROFILE.replace(':bookUuid', bookUuid);
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Error fetching book profile');
    }
  }
);

export const saveBookProfile = createAsyncThunk(
  'userLitGroupAssets/saveBookProfile',
  async ({ bookUuid, profileData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      // Replace the parameter in the URL
      const url = allRoutes.SAVE_API_BOOK_PROFILE.replace(':bookUuid', bookUuid);
      const response = await axios.post(url, profileData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Error saving book profile');
    }
  }
);

export const fetchCompleteBook = createAsyncThunk(
  'userLitGroupAssets/fetchCompleteBook',
  async (bookUuid, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      // Replace the parameter in the URL
      const url = allRoutes.GET_API_COMPLETE_BOOK.replace(':bookUuid', bookUuid);
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Error fetching complete book data');
    }
  }
);

// litGroup selection Tree persistent data

function extractLitGroupById(litGroupUuid, litGroups) {
  let resultLists = { authors: [], litGroup: {}, books: [], characters: { characters: [], variations: [] } }
  if (litGroups === undefined) return resultLists;
  let litGroupsArray = Object.values(litGroups)
  if (litGroupsArray) {
    let litGList = Object.values(litGroupsArray)[0];
    let litGroup = _.find(litGList.has_lit_group, x => {
      if (x.uuid === litGroupUuid) return x;
    })
    if (litGroup === undefined) return resultLists;
    resultLists.authors = { ...litGroup.has_author }
    resultLists.books = { ...litGroup.has_book }
    resultLists.litGroup = litGroup
  }

  return resultLists;
}

function extractLitGroupTypes(litGrpTypes) {
  if (litGrpTypes === undefined) return [];
  return litGrpTypes;
}

// function extractCharsForBook(book, has_characters) {
//   let charList = []
//   if (has_characters !== undefined && book.characters !== undefined && book.characters.length > 0) {
//     _.forEach(book.characters, x => {
//       _.forEach(has_characters, character => {
//         _.forEach(character.has_character, charDetail => {
//           if (charDetail.uuid === x) {
//             charList.push(charDetail)
//           }
//         })

//       })
//     })
//   }
//   return charList;
// }

// const getCharBookGrpList = (CSCample) => {
//   let charListOptions = []
//   if (CSCample && CSCample.charBookGroupLists) {
//     charListOptions = CSCample.charBookGroupLists.map((x) => {
//       let key_value = x.name.replace(/[^a-z0-9]/gi, '').toLowerCase()
//       return { key: key_value, text: x.name, value: key_value, data: x }
//     })
//     return charListOptions;
//   }
//   return [{ key: 'emptyList', text: 'Empty Character List', value: 'emptyList', data: {} }]
// }


const extractLitGroupSpecs = (litGroup) => {
  let resultLists = { authors: [], litGroup: {}, books: [], characters: { characters: [], variations: [] } }
  if (litGroup === undefined || _.isEmpty(litGroup)) return resultLists;
  resultLists.authors = { ...litGroup.has_author }
  resultLists.books = { ...litGroup.has_book }
  // resultLists.characters = { ...resultLists.books.has_characters[0].has_character }

  resultLists.characters = { ...litGroup.has_lit_group_characters[0].has_character }

  let charVariations = []
  Object.values(resultLists.characters).map((charItem, idx) => {
    if (!_.isEmpty(charItem.has_variation)) {
      charVariations.push(charItem.has_variation)
      return charItem.has_variation
    }
    return null
  })
  resultLists.variations = charVariations;
  // console.log(' resultLists.characters', resultLists.characters)
  resultLists.litGroup = litGroup
  return resultLists;
}

function transform(data, lgTypes, hideAuthors = false) {
  let has_lit_grp = data.has_lit_group;
  // let lgTypesArray = Object.values(lgTypes);
  let results = []
  _.forEach(has_lit_grp, litGrp => {

    // console.log('litGrp', litGrp)
    // let litGrpType = _.find(lgTypesArray, x => x.uuid === litGrp.lgType)
    let litGrpType = litGrp.has_lit_grp_type[0]

    if (litGrpType === undefined) litGrpType = ""
    let entry = {
      name: `${litGrp.name} - ${litGrpType.name}`, uuid: litGrp.uuid, aaiType: litGrp.aaiType, children: []
    }
    // get Books
    if (litGrp.has_book !== undefined) {
      let children = []
      _.forEach(litGrp.has_book, book => {
        let bookItem = { name: book.name, aaiType: book.aaiType, uuid: book.uuid, children: [] }
        if (!hideAuthors) {
          _.forEach(book.has_author, child => {
            bookItem.children.push(child)
          })
        }
        // add characters to book's character list, with book's heading
        if (book.has_book_characters !== undefined) {
          let children = []
          _.forEach(book.has_book_characters, character => {
            let characterItem = { name: character.name, uuid: character.uuid, aaiType: "book-characters", children: [] }
            _.forEach(character.has_character, char => {
              let tChar = { ...char, book_uuid: bookItem.uuid }
              characterItem.children.push(tChar)
            })
            children.push(characterItem)
          })
          let charListRoot = { name: children[0].name, uuid: book.uuid, aaiType: "character-list", children: children[0].children }
          bookItem.children.push(charListRoot)
        }
        children.push(bookItem)
      })
      entry.children.push({ name: "Books", uuid: litGrp.uuid, aaiType: "is_lit_group", children })
    }
    // Get characters from the litGrp Character list
    if (litGrp.has_characters !== undefined) {
      let children = []
      _.forEach(litGrp.has_characters, character => {
        let characterItem = { name: character.name, uuid: character.uuid, aaiType: "lit-group-characters", children: [] }
        _.forEach(character.has_character, char => {
          characterItem.children.push(char)
        })
        children.push(characterItem)
      })
      let litGrpCharListRoot = { name: children[0].name, uuid: litGrp.uuid, aaiType: "character-list", children: children[0].children }
      entry.children.push(litGrpCharListRoot)
    }

    results.push(entry)
  })
  return { name: "Literature Groups", children: [{ name: "Literary Groups", aaiType: "lit-groups", children: results }] };
}

// let keyMap = {
//   "has_book": "Books",
//   "has_author": "Authors",
//   "has_characters": "Characters",
//   "has_lit_group": "Literary Groups",
//   "has_character": "Characters",
//   "authors": "",
//   "characters": ""

//   // add other mappings here
// };

const concatStr = (strField, keyMod) => {
  return strField += keyMod.toString();
}

const deriveLitGroupElements = (litGroup) => {
  let derivedOptions = { has_lit_group_characters: [], has_book: [], book_chars: [{ book_uuid: '', book_characters: [] }], has_variations: [] }
  let keyMod = 0;
  if (litGroup.has_lit_group_characters) {
    if (litGroup.has_lit_group_characters[0].has_character) {
      _.forEach(litGroup.has_lit_group_characters, hlgChar => {
        _.forEach(hlgChar.has_character, x => {
          derivedOptions.has_lit_group_characters.push({ key: concatStr(x.uuid, keyMod++), text: x.name, value: x.uuid, aaitype: x.aaiType })
        })
      })
    }
  }

  if (litGroup.has_book) {
    _.forEach(litGroup.has_book, x => {
      derivedOptions.has_book.push({ key: x.uuid, text: x.name, value: x.uuid, aaitype: x.aaiType })

      let bookDetails = { book_uuid: x.uuid, book_characters: [] }
      _.forEach(x.has_book_characters, book_chars => {
        _.forEach(book_chars.has_character, y => {
          bookDetails.book_characters.push({ key: concatStr(y.uuid, keyMod++), text: y.name, value: y.uuid, aaitype: y.aaiType })
        })
      })
      derivedOptions.book_chars.push(bookDetails)
    })
  }

  if (litGroup.has_variations) {
    if (litGroup.has_variations[0].has_variation) {
      _.forEach(litGroup.has_variations, variationSet => {
        _.forEach(variationSet.has_variation, x => {
          derivedOptions.has_variations.push({ key: concatStr(x.uuid, keyMod++), text: x.name, value: x.uuid, aaitype: x.aaiType })
        })
      })
    }
  }

  return derivedOptions;
}

const selectUserAssetsState = state => state.user.userLitGroupAssets.litGroups;
const selectUserMinAssetsState = state => state.user.userLitGroupAssets.litGroups;

const selectStdLitGroupTypesState = state => ({
  litGrpTypes: state.user.userLitGroupAssets.litGrpTypes,
  stdLitGrpTypesStatus: state.user.userLitGroupAssets.stdLitGrpTypesStatus
});
const selectStdMinLitGroupTypesState = state => ({
  litGrpTypes: state.user.userLitGroupAssets.litGrpTypes,
  stdLitGrpTypesStatus: state.user.userLitGroupAssets.stdLitGrpTypesStatus
});

function extractMinLitGroupTreeView(litGroups, litGrpTypes) {
  if (litGroups === undefined) return {};
  let transformed = transform(litGroups[0], litGrpTypes, true) // Pass litGrpTypes here
  return transformed
}

function extractLitGroupTreeView(litGroups, litGrpTypes) {
  if (litGroups === undefined) return {};
  let transformed = transform(litGroups[0], litGrpTypes) // Pass litGrpTypes here
  return transformed
}

export const selectAllListsForLitGroup = createSelector(
  selectUserAssetsState,
  (_, litGroupUuid) => litGroupUuid,
  (userAssets, litGroupUuid) => extractLitGroupById(litGroupUuid, userAssets)
)

export const selectLitGrpData = createSelector(
  (state) => state.user.userLitGroupAssets.userLitGrpTree,
  (userLitGrpTree) => {
    try {
      let { userConstants, curLitGroupUuid, lit_group, lit_groups } = userLitGrpTree;
      let results = { userConstants: userConstants, curLitGroupUuid, litGroup: lit_group, litGroupSpecs: {}, litGroups: lit_groups.has_lit_group };
      results.litGroupSpecs = extractLitGroupSpecs(lit_group)
      // console.log('selectLitGrpData results', results);
      return results;
    }
    catch (e) {
      return {}
    }
  }
)

export const selectAllLitGrpTreeView = createSelector(
  selectUserAssetsState,
  selectStdLitGroupTypesState,
  (userAssets, stdLitGroupTypesState) => extractLitGroupTreeView(userAssets, stdLitGroupTypesState.litGrpTypes)
)

export const selectAllMinLitGrpTreeView = createSelector(
  selectUserMinAssetsState,
  selectStdMinLitGroupTypesState,
  (userAssets, stdLitGroupTypesState) => extractMinLitGroupTreeView(userAssets, stdLitGroupTypesState.litGrpTypes)
)

export const selectLitGrpTypes = createSelector(
  selectStdLitGroupTypesState,
  litGrpTypes => extractLitGroupTypes(litGrpTypes)
);



const userLitGroupAssetsSlice = createSlice({
  name: 'litGroup',
  initialState: {
    entities: [], litGrpTypes: [],
    addLitGrpAndBooksStatus: 'idle',
    addLitGrpAndBooksError: null,
    addVariationToBookCharacterStatus: 'idle',
    userAssetStatus: 'idle',
    userMinAssetStatus: 'idle',
    userMinAssetStatusError: null,
    userLitGrpTree: {},
    userLitGrpTreeStatus: 'idle',
    userLitGrpTreeError: null,
    userAssetStatusError: null,
    userCounts: {},
    userCountsStatus: 'idle',
    userCountsError: null,
    bookData: null,
    bookDataStatus: 'idle',
    bookDataStatusError: null,
    bookCharsVarListStatus: "idle",
    bookCharsVarListStatusError: null,
    charsVarListStatus: "idle",
    charsVarListStatusError: null,
    // charSpeechData: null,
    charSpeechData: {
      speechSample: {
        title: '',
        // other properties...
      },
    },

    // Direct fetch states
    directLitGroups: [],
    directLitGroupsStatus: 'idle',
    directLitGroupsError: null,

    directBooks: [],
    directBooksLitGroupUuid: null,
    directBooksStatus: 'idle',
    directBooksError: null,

    directCharacters: [],
    directCharactersStatus: 'idle',
    directCharactersError: null,

    directVariations: [],
    directVariationsStatus: 'idle',
    directVariationsError: null,

    // Selection state
    selectedLitGroupProfile: null,  // Renamed from selectedLitGroup
    selectedLitGroupUuid: null,     // New property to store just the UUID
    selectedBook: null,
    selectedBookProfile: null,      // New property for book profile
    bookProfileStatus: 'idle',      // Status for book profile operations
    bookProfileError: null,         // Error for book profile operations
    selectedCharacter: null,
    selectedVariation: null,
    selectionId: null,
    persistSelections: true,
    preserveSelections: true,       // New flag to control selection preservation in Redux

    charSpeechDataStatus: 'idle',
    charSpeechDataStatusError: null,
    relatedData: null,
    relatedDataStatus: 'idle',
    relatedDataStatusError: null,
    treeViewSelectionData: null,
    stdLitGrpTypesStatus: 'idle',
    stdLitGrpTypesError: null,
    voiceFormValues: {}, // Holds current values of the Formik component
    isDetailsFormComplete: false, // Indicates if the form is complete based on validation
    clearCounter: 0
  },
  reducers: {
    setTreeViewSelectionData: (state, action) => {
      state.treeViewSelectionData = action.payload;
    },
    setClearVoiceFormValues: (state, action) => {
      state.voiceFormValues.details = {
        "isValid": false,
        "dirty": false,
        "values": {
          "literaryGroup": "",
          "litGroupBook": "",
          "bookCharacter": "",
          "litGroupAllCharacter": "",
          "characterVariation": ""
        }
      }
    },
    setVoiceFormValues: (state, action) => {
      state.voiceFormValues = action.payload;
    },
    setVoiceDetailsFormValues: (state, action) => {

      //   {
      //     "values": {
      //         "literaryGroup": "123e4567-e89b-12d3-a456-426614174007",
      //         "litGroupBook": "123e4567-e89b-12d3-a456-426614174009",
      //         "bookCharacter": "",
      //         "litGroupAllCharacter": "",
      //         "characterVariation": ""
      //     },
      //     "isValid": false,
      //     "dirty": true
      // }

      // const details = {
      //   values: formik.values,
      //   isValid: formik.isValid,
      //   dirty: formik.dirty,
      // }      
      // console.log('setVoiceDetailsFormValues', action.payload)
      state.voiceFormValues.details = action.payload;
      // console.log('setVoiceDetailsFormValues', action.payload)
    },
    setVoiceDetailCharacterVariation: (state, action) => {
      state.voiceFormValues.details.values.characterVariation = action.payload;
    },
    setVoiceDetailBookCharacter: (state, action) => {
      state.voiceFormValues.details.values.bookCharacter = action.payload;
    },
    setVoiceSnippetValues: (state, action) => {
      state.voiceFormValues.snippetValues = action.payload;
    },
    setLitGroupFormUuid: (state, action) => {
      // console.log('userLitGroupAssetsSlice setLitGroupFormUuid', action.payload.requestedObj)
      return { ...state, updateToRequestedObj: action.payload.requestedObj };
    },
    setLitGroupDetail: (state, action) => {
      return { ...state, updateToDetailObj: action.payload.requestedObj };
    },
    setClearCounter: (state, action) => {
      // return { ...state, clearCounter: state.clearCounter++ };
      state.clearCounter = state.clearCounter++;
    },
    setClearRelatedData: (state, action) => {
      state.relatedDataStatus = "idle";
      state.relatedDataStatusError = null;
      state.relatedData = null;
    },
    setClearVoiceLabData: (state, action) => {
      state.relatedDataStatus = "idle";
      state.relatedDataStatusError = null;
      state.relatedData = null;
      state.updateToDetailObj = {
        litGroupItem: {},
        isLitGroup: {},
        bookItem: {},
        characterList: {},
        characterItem: {},
        characterVariation: {}
      }
      state.clearCounter = state.clearCounter++;
    },
    setClearBookData: (state, action) => {
      state.bookDataStatus = 'idle';
      state.bookDataStatusError = null;
      state.bookData = null;
    },
    setSpeechSampleContent: (state, action) => {
      if (!state.charSpeechData) {
        state.charSpeechData = { speechSample: {} };
      }
      if (!state.charSpeechData.speechSample) {
        state.charSpeechData.speechSample = {};
      }
      state.charSpeechData.speechSample.title = action.payload;
      // console.log('setSpeechSampleContent', state.charSpeechData.speechSample.title)
    },
    setSpeechSampleTitle: (state, action) => {
      if (!state.charSpeechData) {
        state.charSpeechData = { speechSample: {} };
      }
      if (!state.charSpeechData.speechSample) {
        state.charSpeechData.speechSample = {};
      }
      state.charSpeechData.speechSample.title = action.payload;
      // console.log('setSpeechSampleTitle', state.charSpeechData.speechSample.title)
    },
    // New error clearing reducers
    clearLitGroupErrors: (state) => {
      state.userLitGrpTreeError = null;
      state.userAssetStatusError = null;
      state.stdLitGrpTypesError = null;
    },
    clearBookErrors: (state) => {
      state.bookDataStatusError = null;
      state.addLitGrpAndBooksError = null;
    },
    clearCharacterErrors: (state) => {
      state.bookCharsVarListStatusError = null;
    },
    clearVariationErrors: (state) => {
      state.charsVarListStatusError = null;
      state.variationListStatusError = null;
    },
    // Reset the shared "direct" asset caches + selections. Dispatched on
    // logout / user switch so a prior session's LitGroups/Books/Characters/
    // Variations never leak into the next user's pages (e.g. the Rendering
    // Studio LitGroup picker, which reads directLitGroups).
    resetDirectAssets: (state) => {
      state.directLitGroups = [];
      state.directLitGroupsStatus = 'idle';
      state.directLitGroupsError = null;
      state.directBooks = [];
      state.directBooksLitGroupUuid = null;
      state.directBooksStatus = 'idle';
      state.directCharacters = [];
      state.directCharactersStatus = 'idle';
      state.directVariations = [];
      state.directVariationsStatus = 'idle';
      state.selectedLitGroupProfile = null;
      state.selectedLitGroupUuid = null;
      state.selectedBook = null;
    },
    // Selection reducers
    setSelection: (state, action) => {

      const { level, id, data } = action.payload;
      state.selectionId = { level, id };

      // Update the corresponding selected entity
      switch (level) {
        case 'litGroup':
          state.selectedLitGroupUuid = id;  // Store just the UUID
          state.selectedLitGroupProfile = null;  // Reset the profile when selection changes
          break;
        case 'book':
          // Store both the UUID and additional data like name
          state.selectedBook = id ? { uuid: id, ...(data || {}) } : null;
          break;
        case 'character':
          state.selectedCharacter = id ? { uuid: id, ...(data || {}) } : null;
          break;
        case 'variation':
          state.selectedVariation = id ? { uuid: id, ...(data || {}) } : null;
          break;
        default:
          break;
      }
    },
    setPersistSelections: (state, action) => {
      state.persistSelections = action.payload;
    },
    setPreserveSelections: (state, action) => {
      state.preserveSelections = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // Get All of Books Characters and their variations plus the charSamples objects
      .addCase(fetchBookCharsVariationSamples.pending, (state) => {
        state.bookCharsVarListStatus = 'loading';
      })
      .addCase(fetchBookCharsVariationSamples.fulfilled, (state, action) => {
        state.bookCharsVarListStatus = 'succeeded';
        // Add any fetched data to the state
        let bookCharVariationList = action.payload;

        // console.log('Completed fetchBookCharsVariationSamples', { ...bookCharVariationList })
        state.bookCharsVarList = { ...bookCharVariationList };
      })
      .addCase(fetchBookCharsVariationSamples.rejected, (state, action) => {
        state.bookCharsVarListStatus = 'failed';
        state.bookCharsVarListStatusError = action.error.message;
      })
      // Get a Books specified Character and its variations plus the charSamples objects

      .addCase(fetchCharVariationSamples.pending, (state) => {
        state.charsVarList = 'loading';
      })
      .addCase(fetchCharVariationSamples.fulfilled, (state, action) => {
        state.charsVarListStatus = 'succeeded';
        // Add any fetched data to the state
        let charVariationList = action.payload;
        state.charsVarList = { ...charVariationList };
      })
      .addCase(fetchCharVariationSamples.rejected, (state, action) => {
        state.charsVarListStatus = 'failed';
        state.charsVarListStatusError = action.error.message;
      })

      // fetcLitGrpVariationList
      .addCase(fetcLitGrpVariationList.pending, (state) => {
        state.variationList = 'loading';
      })
      .addCase(fetcLitGrpVariationList.fulfilled, (state, action) => {
        state.variationListStatus = 'succeeded';
        // Add any fetched data to the state
        let listData = action.payload;
        let ddList = listData.variationList.map(item => ({ key: item.name.toLowerCase(), text: item.name, value: item.name, uuid: item.uuid }))
        state.variationList = { listData, ddList };
      })
      .addCase(fetcLitGrpVariationList.rejected, (state, action) => {
        state.variationListStatus = 'failed';
        state.variationListStatusError = action.error.message;
      })
      .addCase(fetchSpeechSampleData.pending, (state) => {
        state.charSpeechData = 'loading';
      })
      .addCase(fetchSpeechSampleData.fulfilled, (state, action) => {
        state.charSpeechData = 'succeeded';
        // Add any fetched data to the state
        state.charSpeechData = action.payload;
      })
      .addCase(fetchSpeechSampleData.rejected, (state, action) => {
        state.charSpeechDataStatus = 'failed';
        state.charSpeechDataStatusError = action.error.message;
      })
      .addCase(fetchBookData.pending, (state) => {
        state.bookDataStatus = 'loading';
      })
      .addCase(fetchBookData.fulfilled, (state, action) => {
        state.bookDataStatus = 'succeeded';
        // Add any fetched data to the state
        state.bookData = action.payload;
      })
      .addCase(fetchBookData.rejected, (state, action) => {
        state.bookDataStatus = 'failed';
        state.bookDataStatusError = action.error.message;
      })
      .addCase(fetchRelatedData.pending, (state) => {
        state.relatedDataStatus = 'loading';
      })
      .addCase(fetchRelatedData.fulfilled, (state, action) => {
        state.relatedDataStatus = 'succeeded';
        // Add any fetched data to the state
        state.relatedData = action.payload;
      })
      .addCase(fetchRelatedData.rejected, (state, action) => {
        state.relatedDataStatus = 'failed';
        state.relatedDataStatusError = action.error.message;
      })
      .addCase(addLitGroupAndBooks.pending, state => {
        state.addLitGrpAndBooksStatus = 'loading';
      })
      .addCase(addLitGroupAndBooks.fulfilled, (state, action) => {
        state.addLitGrpAndBooksStatus = 'idle';
        state.entities.push(action.payload);
      })
      .addCase(addLitGroupAndBooks.rejected, (state, action) => {
        state.addLitGrpAndBooksStatus = 'idle';
        state.addLitGrpAndBooksError = action.error;
      })
      .addCase(fetchUserAssetTree.pending, (state) => {
        state.userAssetStatus = 'loading';
      })
      .addCase(fetchUserAssetTree.fulfilled, (state, action) => {
        state.userAssetStatus = 'succeeded';
        // Add any fetched data to the state
        state.litGroups = { ...action.payload.userAssets.has_lit_groups };
        state.userLitGroupData = { ...action.payload.litGroupsData };

        // Only clear selections if not preserving
        if (!state.preserveSelections) {
          state.selectedLitGroupUuid = null;
          state.selectedLitGroupProfile = null;
          state.selectedBook = null;
          state.selectedBookProfile = null;
          state.selectedCharacter = null;
          state.selectedVariation = null;
        }
      })
      .addCase(fetchUserAssetTree.rejected, (state, action) => {
        state.userAssetStatus = 'failed';
        state.userAssetStatusError = action.error.message;
      })
      .addCase(fetchUserStdLitGrpTypes.pending, (state) => {
        state.stdLitGrpTypesStatus = 'loading';
      })
      .addCase(fetchUserStdLitGrpTypes.fulfilled, (state, action) => {
        state.stdLitGrpTypesStatus = 'succeeded';
        // Add any fetched data to the state
        let lgTypes = action.payload.litGrpTypes;
        if (lgTypes === null || lgTypes === undefined) return []
        let lgTypOptions = lgTypes.map((x, idx) => {
          return {
            seriesIdx: idx,
            label: x.name,
            value: x.name !== undefined ? x.name.toLowerCase().replace(/\W/g, '') : "",
          }
        })
        state.litGrpTypes = { ...lgTypOptions };
      })
      .addCase(fetchUserStdLitGrpTypes.rejected, (state, action) => {
        state.stdLitGrpTypesStatus = 'failed';
        state.stdLitGrpTypesError = action.error.message;
      })
      .addCase(fetchUserLitGrpTree.pending, (state) => {
        state.userLitGrpTreeStatus = 'loading';
      })
      .addCase(fetchUserLitGrpTree.fulfilled, (state, action) => {
        state.userLitGrpTreeStatus = 'succeeded';
        // Add any fetched data to the state
        let litGrpItems = action.payload.userLitGrpTree.has_lit_groups[0].has_lit_group;
        state.userLitGrpTree = {
          lit_group: _.find(litGrpItems, x => x.uuid === action.payload.reqLitGroupUuid),
          lit_groups: action.payload.userLitGrpTree.has_lit_groups[0],
          curLitGroupUuid: action.payload.reqLitGroupUuid,
          userConstants: action.payload.userConstants[0],
        };

      })
      .addCase(fetchUserLitGrpTree.rejected, (state, action) => {
        state.userLitGrpTreeStatus = 'failed';
        state.userLitGrpTreeError = action.error.message;
      })
      .addCase(updateS3UrlData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateS3UrlData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload; // Assuming you want to store the fetched data
      })
      .addCase(updateS3UrlData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload; // Error message is accessible here
      })
      .addCase(addVariationToBookCharacter.pending, (state) => {
        state.addVariationToBookCharacterStatus = 'loading';
      })
      .addCase(addVariationToBookCharacter.fulfilled, (state, action) => {
        state.addVariationToBookCharacterStatus = 'succeeded';
        state.triggerUpdate += 1; // Trigger re-render
      })
      .addCase(addVariationToBookCharacter.rejected, (state, action) => {
        state.addVariationToBookCharacterStatus = 'failed';
        state.error = action.error.message;
      })

      // Direct fetch reducers
      .addCase(fetchLitGroupsDirect.pending, (state) => {
        state.directLitGroupsStatus = 'loading';
      })
      .addCase(fetchLitGroupsDirect.fulfilled, (state, action) => {
        state.directLitGroupsStatus = 'succeeded';
        state.directLitGroups = action.payload.litGroups;
      })
      .addCase(fetchLitGroupsDirect.rejected, (state, action) => {
        state.directLitGroupsStatus = 'failed';
        state.directLitGroupsError = action.error.message;
      })

      // ⭐ WHOSE books these are, recorded from the moment the fetch STARTS.
      // `directBooks` was a bare array, so nothing downstream could tell a freshly-loaded list from
      // the PREVIOUS group's list still sitting in the store — and the Rendering Studio's
      // auto-select raced exactly there: it re-selected the old group's first book in the gap
      // before the new list arrived, then never corrected itself. Set on `pending` rather than
      // `fulfilled` so the stale window is closed while the request is still in flight.
      .addCase(fetchBooksByLitGroup.pending, (state, action) => {
        state.directBooksLitGroupUuid = action.meta.arg || null;
        state.directBooksStatus = 'loading';
      })
      .addCase(fetchBooksByLitGroup.fulfilled, (state, action) => {
        state.directBooksStatus = 'succeeded';
        state.directBooks = action.payload.books;
      })
      .addCase(fetchBooksByLitGroup.rejected, (state, action) => {
        state.directBooksStatus = 'failed';
        state.directBooksError = action.error.message;
      })

      .addCase(fetchCharactersByBook.pending, (state) => {
        state.directCharactersStatus = 'loading';
      })
      .addCase(fetchCharactersByBook.fulfilled, (state, action) => {
        state.directCharactersStatus = 'succeeded';
        state.directCharacters = action.payload.characters;
      })
      .addCase(fetchCharactersByBook.rejected, (state, action) => {
        state.directCharactersStatus = 'failed';
        state.directCharactersError = action.error.message;
      })

      .addCase(fetchVariationsByCharacter.pending, (state) => {
        state.directVariationsStatus = 'loading';
      })
      .addCase(fetchVariationsByCharacter.fulfilled, (state, action) => {
        state.directVariationsStatus = 'succeeded';
        state.directVariations = action.payload.variations;
      })
      .addCase(fetchVariationsByCharacter.rejected, (state, action) => {
        state.directVariationsStatus = 'failed';
        state.directVariationsError = action.error.message;
      })

      // Literary Group Profile fetch reducers
      .addCase(fetchLitGroupProfile.pending, (state) => {
        state.litGroupProfileStatus = 'loading';
      })
      .addCase(fetchLitGroupProfile.fulfilled, (state, action) => {
        state.litGroupProfileStatus = 'succeeded';
        // Update the selectedLitGroupProfile with the complete profile data
        state.selectedLitGroupProfile = action.payload.profile;
      })
      .addCase(fetchLitGroupProfile.rejected, (state, action) => {
        state.litGroupProfileStatus = 'failed';
        state.litGroupProfileError = action.error.message;
      })

      // Book Profile fetch reducers
      .addCase(fetchBookProfile.pending, (state) => {
        state.bookProfileStatus = 'loading';
      })
      .addCase(fetchBookProfile.fulfilled, (state, action) => {
        state.bookProfileStatus = 'succeeded';
        // Update the selectedBookProfile with the complete profile data
        console.log('Redux - Book profile fetched successfully:', action.payload.profile);
        state.selectedBookProfile = action.payload.profile;
      })
      .addCase(fetchBookProfile.rejected, (state, action) => {
        state.bookProfileStatus = 'failed';
        state.bookProfileError = action.error.message;
      })

      // Book Profile save reducers
      .addCase(saveBookProfile.pending, (state) => {
        state.bookProfileStatus = 'loading';
      })
      .addCase(saveBookProfile.fulfilled, (state, action) => {
        state.bookProfileStatus = 'succeeded';
        state.selectedBookProfile = action.payload.profile;
      })
      .addCase(saveBookProfile.rejected, (state, action) => {
        state.bookProfileStatus = 'failed';
        state.bookProfileError = action.error.message;
      })

      // User counts reducers
      .addCase(fetchUserCounts.pending, (state) => {
        state.userCountsStatus = 'loading';
      })
      .addCase(fetchUserCounts.fulfilled, (state, action) => {
        state.userCountsStatus = 'succeeded';
        state.userCounts = action.payload.counts;
      })
      .addCase(fetchUserCounts.rejected, (state, action) => {
        state.userCountsStatus = 'failed';
        state.userCountsError = action.payload || action.error.message;
      });
  }
});

export const {
  setClearBookData,
  setClearCounter,
  setClearRelatedData,
  setClearVoiceLabData,
  setLitGroupDetail,
  setLitGroupFormUuid,
  setClearVoiceFormValues,
  setVoiceFormValues,
  setVoiceDetailsFormValues,
  setVoiceDetailBookCharacter,
  setVoiceDetailCharacterVariation,
  setVoiceSnippetValues,
  setTreeViewSelectionData,
  setSpeechSampleTitle,
  // New error clearing actions
  clearLitGroupErrors,
  clearBookErrors,
  clearCharacterErrors,
  clearVariationErrors,
  // Selection actions
  setSelection,
  setPersistSelections,
  setPreserveSelections,
  // Cache reset (logout / user switch)
  resetDirectAssets
} = userLitGroupAssetsSlice.actions


// Selectors
export const selectTreeViewSelectionData = (state) => {
  let selTreeViewData = state.user.userLitGroupAssets.treeViewSelectionData;
  return selTreeViewData
}
export const selectSnippetDetails = state => state.user.userLitGroupAssets.voiceFormValues;

export const selectVoiceFormValues = state => state.user.userLitGroupAssets.voiceFormValues;
export const selectIsFormComplete = state => state.user.userLitGroupAssets.isDetailsFormComplete;
export const selectIsVoiceRecDisabled = state => state.user.userLitGroupAssets.isVoiceRecDisabled;
export const selectVoiceDetailsValues = state => {
  const deets = state.user.userLitGroupAssets.voiceFormValues.details;
  return deets;
}

export const selectBookData = state => {
  return state.user.userLitGroupAssets.bookData;
}
export const selectActiveLitGroup = state => {
  return state.user.userLitGroupAssets.updateToRequestedObj;
}
export const selectActiveDetailObj = state => {
  return state.user.userLitGroupAssets.updateToDetailObj;
}
export const selectClearCounter = state => {
  return state.user.userLitGroupAssets.clearCounter;
}
export const selectRelatedData = state => {
  return state.user.userLitGroupAssets.relatedData;
}
export const selectUserLitGroupData = state => {
  return state.user.userLitGroupAssets.userLitGroupData;
}
export const selectCharSpeechData = state => {
  return state.user.userLitGroupAssets.charSpeechData;
}
export const selectVariationList = state => {
  return state.user.userLitGroupAssets.variationList;
}

export const selectBookCharVariationList = state => {
  return state.user.userLitGroupAssets.bookCharsVarList;
}

export const selectCharVariationList = state => {
  return state.user.userLitGroupAssets.charsVarList;
}

// Selection selectors
export const selectPersistSelections = state => state.user.userLitGroupAssets.persistSelections;
export const selectSelectedLitGroupProfile = state => state.user.userLitGroupAssets.selectedLitGroupProfile;
export const selectSelectedLitGroupUuid = state => state.user.userLitGroupAssets.selectedLitGroupUuid;
export const selectSelectedBook = state => state.user.userLitGroupAssets.selectedBook;
export const selectSelectedBookProfile = state => state.user.userLitGroupAssets.selectedBookProfile;
export const selectBookProfileStatus = state => state.user.userLitGroupAssets.bookProfileStatus;
export const selectBookProfileError = state => state.user.userLitGroupAssets.bookProfileError;
export const selectSelectedCharacter = state => state.user.userLitGroupAssets.selectedCharacter;
export const selectSelectedVariation = state => state.user.userLitGroupAssets.selectedVariation;
export const selectSelectionId = (state, level) => {
  const selectionId = state.user.userLitGroupAssets.selectionId;
  return selectionId && selectionId.level === level ? selectionId.id : null;
};

// Direct fetch selectors
export const selectDirectLitGroups = state => state.user.userLitGroupAssets.directLitGroups;
export const selectDirectLitGroupsStatus = state => state.user.userLitGroupAssets.directLitGroupsStatus;
export const selectDirectLitGroupsError = state => state.user.userLitGroupAssets.directLitGroupsError;

export const selectDirectBooks = state => state.user.userLitGroupAssets.directBooks;
/**
 * Which LitGroup `directBooks` currently belongs to — null before any fetch.
 *
 * ⚠️ Compare this against the group you are rendering FOR before trusting `directBooks`. During a
 * group change the two disagree, and acting on the list in that window is what put a stale book
 * behind the Rendering Studio's tree. See the `fetchBooksByLitGroup.pending` case.
 */
export const selectDirectBooksLitGroupUuid = state =>
  state.user.userLitGroupAssets.directBooksLitGroupUuid;
export const selectDirectBooksStatus = state => state.user.userLitGroupAssets.directBooksStatus;
export const selectDirectBooksError = state => state.user.userLitGroupAssets.directBooksError;

export const selectDirectCharacters = state => state.user.userLitGroupAssets.directCharacters;
export const selectDirectCharactersStatus = state => state.user.userLitGroupAssets.directCharactersStatus;
export const selectDirectCharactersError = state => state.user.userLitGroupAssets.directCharactersError;

export const selectDirectVariations = state => state.user.userLitGroupAssets.directVariations;
export const selectDirectVariationsStatus = state => state.user.userLitGroupAssets.directVariationsStatus;
export const selectDirectVariationsError = state => state.user.userLitGroupAssets.directVariationsError;

// User counts selectors
export const selectUserCounts = state => state.user.userLitGroupAssets.userCounts;
export const selectUserCountsStatus = state => state.user.userLitGroupAssets.userCountsStatus;
export const selectUserCountsError = state => state.user.userLitGroupAssets.userCountsError;

export default userLitGroupAssetsSlice.reducer;
